import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { ChannelSettings } from '../../api';
import { useLanguage } from '../../app/i18n';
import { isHiddenMode, useFeatures } from '../../lib/config/features-context';

import { formatWireDecimal } from './display';

/**
 * The note E5 shows when the form starts from the affiliate's recommendation (S7 R5, canvas
 * `PremierReglage.html`): it says what was prefilled and that nothing is validated yet (I7). It
 * takes the place of E7's `[Apply]` banner (`RecommendationBanner`), whose summaries it repeats
 * on purpose so that E7's component stays untouched (S7 R18). No button: the values are already
 * in the form and the trader edits them there.
 */
export function PrefilledNotice({
  recommended,
}: {
  readonly recommended: ChannelSettings;
}): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  // P8 R5: a recommended mode the trader cannot choose is not applied, so it is not named.
  const modeHidden = isHiddenMode(recommended.execution.mode, useFeatures());
  const { sizing, execution } = recommended;
  const sizingText =
    sizing.mode === 'RISK_PERCENT'
      ? t('settings.sizing.summaryRisk', { value: formatWireDecimal(sizing.riskPercent, language) })
      : t('settings.sizing.summaryLots', { value: formatWireDecimal(sizing.lots, language) });
  const executionText =
    execution.mode === 'AUTOMATIC'
      ? t('settings.execution.summaryAutomatic')
      : t('settings.execution.summaryConfirmation');
  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.fx.infoBg,
        borderRadius: '12px',
        px: 1.75,
        py: 1.5,
        fontSize: 13,
        lineHeight: 1.45,
      })}
    >
      <Trans
        i18nKey={
          modeHidden ? 'settings.firstSetup.prefilledSizeOnly' : 'settings.firstSetup.prefilled'
        }
        values={{ sizing: sizingText, execution: executionText }}
        components={{ strong: <Box component="strong" sx={{ fontWeight: 600 }} /> }}
      />
    </Box>
  );
}
